/**
 * EngKbExport — 週次 プロジェクト/タイトル → ナレッジ 出力の
 * プレビュー・実行・履歴・設定を1画面で管理。
 *
 * 出力先ルート（既定）:
 *   技術部 / Project Knowledge / YYYY / Project Name
 *   技術部 / Personal Knowledge / YYYY / Title
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Kanban, Bookmark, ArrowsClockwise, Play, FileText, Warning, ClockCounterClockwise,
  Gear, CircleNotch,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/auth/AuthContext';
import { engApi } from './engApi';

const DAYS_JA = ['月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日', '日曜日'];

const fmtDate = (s) => (s ? s.slice(0, 10) : '');
const weekStartOf = (isoStr) => {
  const d = isoStr ? new Date(isoStr) : new Date();
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0=Sun..6=Sat
  const monOffset = (day === 0 ? 6 : day - 1);
  d.setDate(d.getDate() - monOffset - 7); // 先週の月曜
  return d.toISOString().slice(0, 10);
};
const weekEndOf = (mondayIso) => {
  const d = new Date(mondayIso); d.setDate(d.getDate() + 6);
  return d.toISOString().slice(0, 10);
};

export default function EngKbExport() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [ws, setWs] = useState(() => weekStartOf());
  const [we, setWe] = useState(() => weekEndOf(weekStartOf()));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    engApi.kbExportSettings().then(setSettings).catch(() => {});
    engApi.kbExportHistory({ limit: 30 }).then(setHistory).catch(() => setHistory([]));
  }, []);

  const onWsChange = (v) => { setWs(v); setWe(weekEndOf(v)); };

  const runPreview = async () => {
    if (!ws || !we) return;
    setLoading(true); setPreview(null);
    try {
      const p = await engApi.kbExportPreview({ week_start: ws, week_end: we });
      setPreview(p);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'プレビューの取得に失敗しました');
    } finally { setLoading(false); }
  };

  const runExport = async () => {
    if (!preview) return;
    if (!window.confirm(`${preview.items.length}件のナレッジ記事を作成/更新します。よろしいですか？`)) return;
    setRunning(true);
    try {
      const r = await engApi.kbExportRun({ week_start: ws, week_end: we });
      toast.success(`ナレッジ出力を実行しました — 作成 ${r.created_article_ids.length}件 / 更新 ${r.updated_article_ids.length}件`);
      setPreview(null);
      engApi.kbExportHistory({ limit: 30 }).then(setHistory).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'ナレッジ出力に失敗しました');
    } finally { setRunning(false); }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const saved = await engApi.saveKbExportSettings({
        enabled: !!settings.enabled,
        export_mode: settings.export_mode || 'manual_only',
        export_day_of_week: Number(settings.export_day_of_week ?? 4),
        project_root_path: settings.project_root_path || '技術部/Project Knowledge',
        personal_root_path: settings.personal_root_path || '技術部/Personal Knowledge',
        year_handling: settings.year_handling || 'week_start_year',
        include_source_links: settings.include_source_links !== false,
        require_preview_before_manual_export: settings.require_preview_before_manual_export !== false,
        allow_scheduled_export_without_preview: settings.allow_scheduled_export_without_preview !== false,
      });
      setSettings(saved);
      toast.success('ナレッジ出力設定を保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '設定の保存に失敗しました');
    } finally { setSavingSettings(false); }
  };

  const groups = useMemo(() => {
    if (!preview) return { projects: [], titles: [] };
    return {
      projects: preview.items.filter((x) => x.kind === 'project'),
      titles: preview.items.filter((x) => x.kind === 'title'),
    };
  }, [preview]);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="eng-kb-export-page">
      <div>
        <h1 className="text-lg font-bold text-zinc-900">週次更新をナレッジへ出力</h1>
        <p className="text-xs text-zinc-500 mt-1">
          日報を集計し、プロジェクト由来は <code className="text-[10px]">技術部 / Project Knowledge / 年 / プロジェクト名</code>、
          タイトル由来は <code className="text-[10px]">技術部 / Personal Knowledge / 年 / タイトル</code> に出力します。
        </p>
      </div>

      <div className="border border-zinc-200 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">週の開始</Label>
            <Input type="date" value={ws} onChange={(e) => onWsChange(e.target.value)} className="h-9 mt-1" data-testid="kb-week-start" />
          </div>
          <div>
            <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">週の終わり</Label>
            <Input type="date" value={we} onChange={(e) => setWe(e.target.value)} className="h-9 mt-1" data-testid="kb-week-end" />
          </div>
          <Button onClick={runPreview} disabled={loading}
            className="bg-teal-700 hover:bg-teal-800" data-testid="kb-preview-btn">
            {loading ? <><CircleNotch size={14} className="mr-1 animate-spin" /> 集計中…</> : <><FileText size={14} className="mr-1" /> 出力プレビュー</>}
          </Button>
          {preview && (
            <Button onClick={runExport} disabled={running || preview.items.length === 0}
              className="bg-teal-700 hover:bg-teal-800" data-testid="kb-run-btn">
              {running ? <><CircleNotch size={14} className="mr-1 animate-spin" /> 実行中…</> : <><Play size={14} className="mr-1" weight="fill" /> 出力を実行 ({preview.items.length})</>}
            </Button>
          )}
        </div>
        <p className="text-[10px] text-zinc-500">
          プレビューはドライラン — ナレッジ記事は変更されません。「出力を実行」で初めて記事が作成/更新されます。
        </p>
      </div>

      {preview && (
        <div className="space-y-4" data-testid="kb-preview-panel">
          <div className="flex flex-wrap gap-3 text-xs">
            <StatChip n={preview.totals.projects} label="Projects" tone="indigo" />
            <StatChip n={preview.totals.titles} label="Titles" tone="teal" />
            <StatChip n={preview.totals.created} label="新規記事" tone="emerald" />
            <StatChip n={preview.totals.updated} label="更新記事" tone="blue" />
            {preview.totals.skipped_reports > 0 && (
              <StatChip n={preview.totals.skipped_reports} label="スキップ日報" tone="rose" />
            )}
          </div>

          {preview.warnings.length > 0 && (
            <div className="border border-amber-200 bg-amber-50/60 rounded p-3 text-xs text-amber-800">
              <div className="font-semibold mb-1 inline-flex items-center gap-1"><Warning size={12} weight="fill" /> 警告</div>
              <ul className="list-disc list-inside">{preview.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}

          <PreviewGroup title="Project Knowledge" icon={<Kanban size={14} weight="fill" className="text-indigo-500" />}
            items={groups.projects} tone="indigo" />
          <PreviewGroup title="Personal Knowledge" icon={<Bookmark size={14} weight="fill" className="text-teal-600" />}
            items={groups.titles} tone="teal" />

          {preview.skipped.length > 0 && (
            <div className="border border-zinc-200 rounded p-3 text-xs">
              <div className="font-semibold text-zinc-700 mb-1">スキップされた日報 ({preview.skipped.length})</div>
              <ul className="space-y-0.5 text-zinc-500">
                {preview.skipped.map((s) => (
                  <li key={s.id}>{s.report_date} · {s.engineer_name} — {s.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {isAdmin && settings && (
        <div className="border border-zinc-200 rounded-lg p-4 space-y-3" data-testid="kb-settings-panel">
          <div className="flex items-center gap-2">
            <Gear size={16} weight="duotone" className="text-zinc-500" />
            <h2 className="text-sm font-bold">ナレッジ出力設定</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-2 border rounded">
              <Label className="text-xs">週次ナレッジ自動出力を有効化</Label>
              <Switch checked={!!settings.enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
                data-testid="kb-set-enabled" />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">モード</Label>
              <Select value={settings.export_mode || 'manual_only'} onValueChange={(v) => setSettings((s) => ({ ...s, export_mode: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="kb-set-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual_only">手動のみ</SelectItem>
                  <SelectItem value="scheduled_weekly">週次自動出力</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">出力曜日</Label>
              <Select value={String(settings.export_day_of_week ?? 4)} onValueChange={(v) => setSettings((s) => ({ ...s, export_day_of_week: Number(v) }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="kb-set-day"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS_JA.map((n, i) => <SelectItem key={i} value={String(i)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">年度の扱い</Label>
              <Select value={settings.year_handling || 'week_start_year'} onValueChange={(v) => setSettings((s) => ({ ...s, year_handling: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="kb-set-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="week_start_year">週の開始年を使用</SelectItem>
                  <SelectItem value="report_year">出力実行年を使用</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Project Knowledge ルート</Label>
              <Input value={settings.project_root_path || ''} onChange={(e) => setSettings((s) => ({ ...s, project_root_path: e.target.value }))}
                className="h-9 mt-1 font-mono text-xs" data-testid="kb-set-project-root" />
            </div>
            <div>
              <Label className="text-[11px] font-bold text-zinc-500 uppercase tracking-wider">Personal Knowledge ルート</Label>
              <Input value={settings.personal_root_path || ''} onChange={(e) => setSettings((s) => ({ ...s, personal_root_path: e.target.value }))}
                className="h-9 mt-1 font-mono text-xs" data-testid="kb-set-personal-root" />
            </div>
            <div className="flex items-center justify-between p-2 border rounded">
              <Label className="text-xs">日報リンクを出力に含める</Label>
              <Switch checked={settings.include_source_links !== false}
                onCheckedChange={(v) => setSettings((s) => ({ ...s, include_source_links: v }))}
                data-testid="kb-set-source-links" />
            </div>
            <div className="flex items-center justify-between p-2 border rounded">
              <Label className="text-xs">手動出力前にプレビュー必須</Label>
              <Switch checked={settings.require_preview_before_manual_export !== false}
                onCheckedChange={(v) => setSettings((s) => ({ ...s, require_preview_before_manual_export: v }))}
                data-testid="kb-set-require-preview" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={saveSettings} disabled={savingSettings} className="bg-zinc-800 hover:bg-zinc-900"
              data-testid="kb-set-save">
              {savingSettings ? '保存中…' : '設定を保存'}
            </Button>
          </div>
        </div>
      )}

      <div className="border border-zinc-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ClockCounterClockwise size={16} weight="duotone" className="text-zinc-500" />
          <h2 className="text-sm font-bold">出力履歴</h2>
        </div>
        {history.length === 0 && <p className="text-xs text-zinc-500">履歴がまだありません。</p>}
        <table className="w-full text-xs" data-testid="kb-history-table">
          <thead className="bg-zinc-100 text-zinc-700">
            <tr>
              <th className="text-left px-2 py-1">日付</th>
              <th className="text-left px-2 py-1">対象期間</th>
              <th className="text-left px-2 py-1">実行種別</th>
              <th className="text-left px-2 py-1">実行者</th>
              <th className="text-left px-2 py-1">状態</th>
              <th className="text-right px-2 py-1">新規</th>
              <th className="text-right px-2 py-1">更新</th>
              <th className="text-right px-2 py-1">失敗</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200">
            {history.map((h) => (
              <tr key={h.id} data-testid={`kb-history-row-${h.id}`}>
                <td className="px-2 py-1 tabular-nums">{fmtDate(h.export_date)}</td>
                <td className="px-2 py-1 text-zinc-500">{fmtDate(h.week_start)}〜{fmtDate(h.week_end)}</td>
                <td className="px-2 py-1">{h.export_type === 'scheduled' ? '週次自動' : '手動'}</td>
                <td className="px-2 py-1">{h.triggered_by_name || '—'}</td>
                <td className="px-2 py-1">
                  <StatusBadge status={h.status} />
                </td>
                <td className="px-2 py-1 text-right tabular-nums">{(h.created_article_ids || []).length}</td>
                <td className="px-2 py-1 text-right tabular-nums">{(h.updated_article_ids || []).length}</td>
                <td className="px-2 py-1 text-right tabular-nums text-rose-600">{(h.failed_items || []).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatChip({ n, label, tone }) {
  const tones = {
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border font-semibold tabular-nums ${tones[tone]}`}>
      <span className="text-sm">{n}</span> <span className="text-[10px]">{label}</span>
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    success: { c: 'bg-emerald-50 text-emerald-700 border-emerald-200', t: '成功' },
    partial_success: { c: 'bg-amber-50 text-amber-700 border-amber-200', t: '一部成功' },
    failed: { c: 'bg-rose-50 text-rose-700 border-rose-200', t: '失敗' },
  };
  const s = map[status] || map.success;
  return <Badge variant="outline" className={`${s.c} text-[10px]`}>{s.t}</Badge>;
}

function PreviewGroup({ title, icon, items, tone }) {
  if (items.length === 0) return null;
  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden">
      <div className={`px-3 py-2 bg-${tone}-50/60 border-b border-${tone}-100 text-sm font-semibold text-zinc-800 flex items-center gap-2`}>
        {icon}{title} <span className="text-xs text-zinc-500 font-normal">({items.length})</span>
      </div>
      <table className="w-full text-xs">
        <thead className="bg-zinc-100 text-zinc-700">
          <tr>
            <th className="text-left px-3 py-1.5">名称</th>
            <th className="text-left px-3 py-1.5">出力先パス</th>
            <th className="text-right px-3 py-1.5">日報数</th>
            <th className="text-left px-3 py-1.5">関係者</th>
            <th className="text-left px-3 py-1.5">動作</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200">
          {items.map((it) => (
            <tr key={`${it.kind}-${it.id}`} data-testid={`kb-preview-row-${it.id}`}>
              <td className="px-3 py-1.5 font-medium text-zinc-800">{it.display}</td>
              <td className="px-3 py-1.5 font-mono text-[11px] text-zinc-500">{it.destination_path}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{it.report_count}</td>
              <td className="px-3 py-1.5 text-zinc-500 truncate max-w-xs">{it.contributors.join('、') || '—'}</td>
              <td className="px-3 py-1.5">
                <Badge variant="outline" className={it.action === 'created'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]'
                  : 'bg-blue-50 text-blue-700 border-blue-200 text-[10px]'}>
                  {it.action === 'created' ? '新規週セクション' : '同一週を更新'}
                </Badge>
                {it.article_created && <Badge variant="outline" className="ml-1 bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">記事を新規作成</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
